import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { localizedProps, pick } from "src/utils/localize";
import { formatPrice, optionPrice, parsePrice } from "src/utils/price";
import PatternLayer from "src/sections/components/pattern-layer";

// ДОПОЛНИТЕЛЬНЫЕ ОПЦИИ ТУРА И ЖИВОЙ ПОДСЧЁТ СТОИМОСТИ.
//
// Зачем это здесь. У конкурентов дополнительные услуги висят строчками в
// прайсе: «трансфер — по запросу», «улучшенный номер — уточняйте». Человек не
// может понять, во что ему обойдётся ПОЕЗДКА, пока не напишет менеджеру.
// Здесь он отмечает нужное галочкой и сразу видит итог — и этот же список
// уезжает вместе с заявкой, так что менеджеру не нужно переспрашивать.
//
// Данные приходят из админки (Activity.extras), формат — в backend/app/models.py:
//   { type: "checkbox" | "select", label: {ru,…}, note: {ru,…}, price: "150 $",
//     options: [{ label: {ru,…}, price: "…" }] }
//
// Цены — строки («150 $», «20 000 ₸»). Складывать их умеет src/utils/price.js:
// он вынимает число, а итог собирает обратно в том же виде. Если цена не
// разбирается («по запросу»), опция всё равно выбирается — она просто не
// прибавляется к сумме, а под итогом появляется оговорка.

const isSelect = (extra) => extra?.type === "select" && extra?.options?.length > 0;

/**
 * Состояние калькулятора. Живёт в родителе (activity-info), потому что итог
 * нужен двум местам сразу: этому блоку и кнопке «Забронировать» — с ней
 * выбранные опции уходят в заявку.
 */
export const useTourExtras = (extras, basePrice, lang) => {
  const list = useMemo(() => (Array.isArray(extras) ? extras : []), [extras]);
  // { [индекс опции]: true | номер варианта }
  const [selection, setSelection] = useState({});

  const toggle = (index) =>
    setSelection((prev) => ({ ...prev, [index]: !prev[index] }));

  const choose = (index, optionIndex) =>
    setSelection((prev) => ({
      // повторный клик по выбранному варианту снимает выбор
      ...prev,
      [index]: prev[index] === optionIndex ? undefined : optionIndex,
    }));

  const reset = () => setSelection({});

  const result = useMemo(() => {
    const base = parsePrice(basePrice);
    const chosen = [];
    let total = base.amount ?? 0;
    let hasUnpriced = false;

    list.forEach((extra, i) => {
      const value = selection[i];
      if (isSelect(extra)) {
        if (value == null || value === false) return;
        const option = extra.options[value];
        if (!option) return;
        const price = optionPrice(option.price);
        if (price.hasPrice) total += price.amount;
        else hasUnpriced = true;
        chosen.push({
          label: `${pick(extra.label, lang)}: ${pick(option.label, lang)}`,
          price,
        });
      } else {
        if (!value) return;
        const price = optionPrice(extra.price);
        if (price.hasPrice) total += price.amount;
        else hasUnpriced = true;
        chosen.push({ label: pick(extra.label, lang), price });
      }
    });

    return {
      base,
      chosen,
      hasUnpriced,
      count: chosen.length,
      total,
      // Итог показываем только если базовая цена вообще разобралась в число:
      // у тура «по запросу» складывать нечего.
      totalText: base.amount == null ? "" : formatPrice(total, base),
    };
  }, [list, selection, basePrice, lang]);

  return { list, selection, toggle, choose, reset, ...result };
};

/** Строка выбранных опций для заявки и для WhatsApp. */
export const extrasSummaryText = (state, labels) => {
  if (!state?.chosen?.length) return "";
  const lines = state.chosen.map(
    (item) => `• ${item.label}${item.price.display ? ` — ${item.price.display}` : ""}`
  );
  const head = `${labels.title}:`;
  const tail = state.totalText ? `\n${labels.total}: ${state.totalText}` : "";
  return `${head}\n${lines.join("\n")}${tail}`;
};

// Квадратик галочки. Настоящий <input> лежит рядом невидимым: клавиатура,
// скринридер и автотесты работают с ним, а рисуем мы этот квадрат.
const Box = ({ checked }) => (
  <span
    aria-hidden="true"
    className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-[5px] border transition-colors ${
      checked
        ? "border-kj-gold bg-kj-gold text-kj-dark"
        : "border-kj-line bg-kj-dark/40 text-transparent"
    }`}
  >
    <Check className="h-3.5 w-3.5" strokeWidth={3} />
  </span>
);

// Цена справа в строке: сумма, «бесплатно» или «по запросу».
const PriceTag = ({ price, muted = false }) => {
  const { t } = useTranslation();
  const text = price.free
    ? t("tour.extrasFree")
    : price.hasPrice
    ? `+ ${price.display}`
    : price.display || t("tour.extrasRequest");

  return (
    <span
      className={`flex-none text-sm font-bold tabular-nums whitespace-nowrap ${
        muted || !price.hasPrice || price.free ? "text-kj-sub" : "text-kj-gold"
      }`}
    >
      {text}
    </span>
  );
};

export default function TourExtras({ state, className = "" }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  if (!state?.list?.length) return null;

  return (
    <section
      className={`relative overflow-hidden rounded-2xl border border-kj-line bg-kj-card/50 p-5 sm:p-7 ${className}`}
    >
      <PatternLayer kind="asanoha" />
      <div className="relative flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-lg sm:text-xl font-extrabold uppercase tracking-tight text-kj-ink">
          {t("tour.extrasTitle")}
        </h3>
        {state.count > 0 && (
          <button
            type="button"
            onClick={state.reset}
            className="text-xs font-bold uppercase tracking-[0.1em] text-kj-sub hover:text-kj-gold transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold"
          >
            {t("filters.reset")}
          </button>
        )}
      </div>
      <p className="relative mt-2 text-sm text-kj-sub leading-relaxed max-w-[62ch]">
        {t("tour.extrasHint")}
      </p>

      <ul className="relative mt-5 flex flex-col divide-y divide-kj-line/60 border-y border-kj-line/60">
        {state.list.map((extra, i) => {
          const label = localizedProps(extra?.label, lang);
          const note = localizedProps(extra?.note, lang);

          // ВАРИАНТ СО СПИСКОМ: одна опция — несколько взаимоисключающих
          // вариантов (класс отеля, тип номера, длительность трансфера).
          if (isSelect(extra)) {
            return (
              <li key={i} className="py-4 sm:py-5">
                <fieldset>
                  <legend
                    {...label.attrs}
                    className="text-sm sm:text-base font-bold text-kj-ink"
                  >
                    {label.value}
                  </legend>
                  {note.value && (
                    <p {...note.attrs} className="mt-1 text-xs sm:text-sm text-kj-sub leading-snug">
                      {note.value}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {extra.options.map((option, oi) => {
                      const optionLabel = localizedProps(option?.label, lang);
                      const price = optionPrice(option?.price);
                      const active = state.selection[i] === oi;
                      return (
                        <label
                          key={oi}
                          className={`group flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 min-h-[44px] text-sm transition-colors ${
                            active
                              ? "border-kj-gold bg-kj-gold/10 text-kj-ink"
                              : "border-kj-line bg-kj-dark/30 text-kj-sub hover:border-kj-gold/60 hover:text-kj-ink"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`extra-${i}`}
                            checked={active}
                            onChange={() => state.choose(i, oi)}
                            onClick={() => active && state.choose(i, oi)}
                            className="sr-only"
                          />
                          <span {...optionLabel.attrs} className="font-semibold">
                            {optionLabel.value}
                          </span>
                          <PriceTag price={price} muted={!active} />
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              </li>
            );
          }

          // ОБЫЧНАЯ ГАЛОЧКА. Вся строка кликабельная — на телефоне попасть
          // в квадратик 20×20 пальцем неудобно.
          const checked = Boolean(state.selection[i]);
          const price = optionPrice(extra?.price);
          return (
            <li key={i}>
              <label className="flex cursor-pointer items-start gap-3 py-4 group">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => state.toggle(i)}
                  className="sr-only"
                />
                <Box checked={checked} />
                <span className="min-w-0 flex-1">
                  <span
                    {...label.attrs}
                    className={`block text-sm sm:text-base font-semibold transition-colors ${
                      checked ? "text-kj-ink" : "text-kj-sub group-hover:text-kj-ink"
                    }`}
                  >
                    {label.value}
                  </span>
                  {note.value && (
                    <span
                      {...note.attrs}
                      className="mt-1 block text-xs sm:text-sm text-kj-sub leading-snug"
                    >
                      {note.value}
                    </span>
                  )}
                </span>
                <PriceTag price={price} muted={!checked} />
              </label>
            </li>
          );
        })}
      </ul>

      {/* ИТОГ. Базовая цена сверху мелко, итог — крупно: глаз должен цепляться
          за сумму, которую человек в результате заплатит. */}
      <div className="relative mt-5 rounded-xl border border-kj-gold/40 bg-kj-gold/[0.07] p-4 sm:p-5">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-kj-sub">
              {t("tour.extrasBase")}
            </p>
            <p className="mt-0.5 text-sm text-kj-sub tabular-nums">
              {state.base.raw || "—"}
            </p>
          </div>
          {state.totalText && (
            <div className="text-right ml-auto">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-kj-sub">
                {t("tour.extrasTotal")}
              </p>
              <p className="mt-0.5 text-2xl sm:text-3xl font-extrabold text-kj-gold tabular-nums leading-none">
                {state.totalText}
                {state.hasUnpriced && (
                  <span className="ml-1.5 align-middle text-xs font-semibold text-kj-sub">
                    + {t("tour.extrasRequest")}
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        <p className="mt-3 border-t border-kj-gold/25 pt-3 text-xs sm:text-sm text-kj-sub leading-relaxed">
          {state.count === 0
            ? t("tour.extrasNone")
            : `${t("tour.extrasSelected", { count: state.count })} · ${t(
                "tour.extrasInRequest"
              )}`}
          <br />
          {t("tour.extrasNote")}
        </p>
      </div>
    </section>
  );
}
